import React, { useEffect, useState, useCallback, useMemo } from "react";
import Checkbox from "../common/Checkbox";
import SearchField from "../common/SearchField";
import Scrollbar from "react-scrollbars-custom";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "../../Layout/Loader";
import { useSelector, shallowEqual } from "react-redux";
import Style from "style-it";

const DeviceSelectorFXAsset = ({
  reportType,
  selectedDevices,
  onSelectDevice,
  translate,
}) => {
  const devices3 = useSelector((state) => state.devices3 || [], shallowEqual);
  const themecolors = useSelector((state) => state.themeColors);

  const memoizedDevices3 = useMemo(() => devices3, [devices3]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [displayed, setDisplayed] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectAll, setSelectAll] = useState(false);

  const PAGE_SIZE = 300;

  const getFilteredDevices = useCallback(() => {
    let list = memoizedDevices3;
    if (reportType === 25) {
      list = list.filter((d) => d?.model === "FX Asset");
    }
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      list = list.filter((d) => d.name?.toLowerCase().includes(lower));
    }
    return list;
  }, [memoizedDevices3, reportType, searchTerm]);

  useEffect(() => {
    const result = getFilteredDevices();
    setFiltered(result);
    setDisplayed(result.slice(0, PAGE_SIZE));
    setHasMore(result.length > PAGE_SIZE);
    setPage(1);

    const allIds = result.map((d) => d.id);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedDevices.includes(id));
    setSelectAll(allSelected);
  }, [getFilteredDevices, selectedDevices]);

  const fetchMore = () => {
    const nextPage = page + 1;
    const start = (nextPage - 1) * PAGE_SIZE;
    const end = nextPage * PAGE_SIZE;

    if (start >= filtered.length) {
      setHasMore(false);
      return;
    }

    setDisplayed((prev) => [...prev, ...filtered.slice(start, end)]);
    setPage(nextPage);
  };

  const handleSelectDevice = (id) => {
    let updated;
    if (selectedDevices.includes(id)) {
      updated = selectedDevices.filter((d) => d !== id);
    } else {
      updated = [...selectedDevices, id];
    }
    onSelectDevice(updated);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const filteredIds = filtered.map((d) => d.id);

    if (checked) {
      const merged = Array.from(new Set([...selectedDevices, ...filteredIds]));
      onSelectDevice(merged);
    } else {
      const remaining = selectedDevices.filter((id) => !filteredIds.includes(id));
      onSelectDevice(remaining);
    }
  };

  return Style.it(
    `
      .selector-list li > label.active,
      .selector-list li > label:hover {
        background: ${themecolors?.themeLightColor || "#007bff"};
        color: ${themecolors?.themeLightInverse || "#fff"};
      }
      .selector-list li {
        margin-bottom: 6px;
      }
      .selector-list li > label {
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 4px 6px;
        border-radius: 4px;
        background: ${themecolors?.themeListingColor || "#f6f6f6"};
      }
      .selector-list {
        padding: 0;
        margin: 0;
        list-style: none;
        color: ${themecolors?.themeListingInverse || "#333"};
      }
      .selector-label {
        font-size: 12px;
        line-height: 1.5;
        margin-left: 6px;
      }
      .selector-image img {
        height: 26px;
        width: 26px;
        display: block;
      }
      .selector-check {
        padding: 2px 4px;
      }
      .selector-count {
        padding: 4px 0;
        font-size: 11px;
        display: flex;
        justify-content: space-between;
      }
    `,
    <div style={{ width: "100%" }}>
      <div
        className="section-head clearfix section-head-filter"
        style={{ padding: 0, marginLeft: -6, marginRight: -2 }}
      >
        <ul className="filter-row pull-left">
            <li>
              <SearchField
                label={translate?.("searchUnits") || "Search"}
                type="search"
                placeholder="exp: ID, Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </li>
        
          <li style={{ paddingRight: 0, paddingTop: 3 }}>
            <Checkbox
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              label={translate?.("selectAll") || "Select All"}
            />
          </li>
        </ul>
      </div>
      <div className="selector-count">
        <span>
          <span style={{ marginRight: 3 }}>{selectedDevices.length}</span>
          {translate?.("UnitsSelected") || "Selected"}
        </span>
        {/* <span>
          <span style={{ marginRight: 3 }}>{filtered.length}</span>
          {translate?.("TotalUnits") || "Total"}
        </span> */}
      </div>
      <Scrollbar
        disableTracksWidthCompensation={true}
        style={{ height: 32 * 10 }}
        scrollerProps={{
          renderer: (propss) => {
            const { elementRef, ...restProps } = propss;
            return (
              <div {...restProps} ref={elementRef} id="scrollableDeviceSelector" />
            );
          },
        }}
      >
        {filtered.length ? (
          <InfiniteScroll
            dataLength={displayed.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={<Loader defaultStyle={true} />}
            scrollableTarget="scrollableDeviceSelector"
          >
            <ul className="selector-list">
              {displayed.map((row) => (
                <li key={row.id}>
                  <label className={selectedDevices.includes(row.id) ? "active" : ""}>
                    <span className="selector-check">
                      <Checkbox
                        checked={selectedDevices.includes(row.id)}
                        onChange={() => handleSelectDevice(row.id)}
                      />
                    </span>
                    <span className="selector-image">
                      <img
                        src={`/assets/category/default/${row.category || "default"}.svg`}
                        alt={row.name}
                      />
                    </span>
                    <span className="selector-label">{row.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          </InfiniteScroll>
        ) : (
          <p style={{ textAlign: "center", color: "#ccc" }}>
            {translate?.("notFound") || "No FX Asset devices found"}
          </p>
        )}
      </Scrollbar>
    </div>
  );
};

export default React.memo(DeviceSelectorFXAsset);
